"use client";
import {
  calculateAiBudgetPercent,
  convertDate,
  findEventTypeOption,
  getServicePackageName,
  useLocalStorage,
  type Translationtype,
} from "utils";
import { useReservationContext } from "../../context";
import { useEffect, useState } from "react";
import { useLocale } from "next-intl";
import Link from "next/link";
import { BsStarFill } from "react-icons/bs";
import { eventTypeOptions } from "../../constants";
import { SimpleSelectedReservation } from "./SimpleSelectedReservation";
import { JinnyMagicAiModal } from "./JinnyMagicAiModal";
import { getMyReservationById, getReservationById } from "../../services";
import { useAppConfiguration } from "appConfig";
import { InfoTooltipButton } from "../InfoTooltipButton";

interface ISelectedReservationCardProps {
  t: Translationtype;
  session: any;
  simpleMode?: boolean;
}

export function SelectedReservationCard({
  t,
  session,
  simpleMode = false,
}: ISelectedReservationCardProps) {
  const locale = useLocale();
  const { reservationId, storeMagicAiStatus } = useReservationContext();
  const [selectedReservation, setSelectedReservation] = useState<any>();
  const [showPremiumModal, setShowPremiumModal] = useLocalStorage(
    "showPremiumModal",
    true
  );

  const isAdmin = session?.user?.isAdmin;

  useEffect(() => {
    if (!reservationId) return;
    let cancelled = false;

    const fetchReservation = isAdmin ? getReservationById : getMyReservationById;
    fetchReservation(reservationId).then((resp) => {
      if (cancelled) return;
      setSelectedReservation(resp);
      storeMagicAiStatus(resp?.magicServices);
    });

    return () => { cancelled = true; };
  }, [reservationId, isAdmin, storeMagicAiStatus]);

  const { isJinnyApp } = useAppConfiguration();

  if (!selectedReservation) {
    return null;
  }

  return (
    <div className="my-5">
      {simpleMode ? (
        <SimpleSelectedReservation
          selectedReservation={selectedReservation}
          t={t}
        />
      ) : (
        <>
          <h1 className="text-[#1e293b] text-center text-2xl font-semibold mb-2">
            {t("Global.selectedReservation")}
          </h1>
          <article className="max-w-xs mx-auto rounded-tl-xl rounded-tr-xl overflow-hidden shadow-lg">
            <h2 className="text-lg bg-[#1e293b] py-2 font-semibold text-white text-center">
              {selectedReservation?.eventType !== -1
                ? t(
                    `Options.event.${findEventTypeOption(selectedReservation?.eventType, eventTypeOptions)}` as any
                  )
                : selectedReservation?.title || ""}
            </h2>
            <div className="bg-brownMild py-4">
              <h2 className="text-[#1e293b] text-center text-lg">
                {t("Global.eventDate")}
              </h2>
              <p className="font-bold text-[#1e293b] text-center text-xl">
                {`${convertDate(selectedReservation?.eventStartTime, "utcL", locale)} - ${convertDate(
                  selectedReservation?.eventEndTime,
                  "utcL",
                  locale
                )}`}
              </p>
              <h2 className="text-[#1e293b] text-center text-lg mt-4">
                {t("Global.package")}
              </h2>
              <p className="font-bold text-[#1e293b] text-center text-xl">
                {selectedReservation?.aiBudgetPriceName
                  ? `Magic AI - ${selectedReservation.aiBudgetPriceName}`
                  : getServicePackageName(selectedReservation?.magicServices, t)}
              </p>
              {(() => {
                const budgetPercent = calculateAiBudgetPercent(
                  selectedReservation?.magicServices,
                  selectedReservation?.aiBudgetUsd,
                  selectedReservation?.cost,
                  selectedReservation?.aiBudgetPriceId
                );
                if (budgetPercent === null) return null;
                return (
                  <>
                    <div className="flex items-center justify-center gap-1.5 mt-4">
                      <h2 className="text-[#1e293b] text-lg">
                        {t("Reservation.AIBudgetPercent")}
                      </h2>
                      <InfoTooltipButton text={t("Global.aiBudgetInfoTooltip")} />
                    </div>
                    {budgetPercent === "unlimited" ? (
                      <p className="font-bold text-[#1e293b] text-center text-xl">
                        ∞
                      </p>
                    ) : (
                      <p className={`font-bold text-center text-xl ${
                        budgetPercent <= 10
                          ? "text-red-500"
                          : budgetPercent <= 30
                          ? "text-orange-500"
                          : "text-green-600"
                      }`}>
                        {budgetPercent}%
                      </p>
                    )}
                  </>
                );
              })()}
              <Link
                className="flex flex-row gap-2 items-center w-fit border-[#1e293b] border-2 rounded-full mx-auto px-4 py-1 my-5 text-[#1e293b] font-semibold hover:bg-[#1e293b] hover:text-white"
                href="/manageMagicAI"
              >
                <BsStarFill />
                {t("Global.topUpAI")}
              </Link>
            </div>
          </article>
        </>
      )}
      {isJinnyApp ? (
        <JinnyMagicAiModal
          t={t}
          show={showPremiumModal}
          onHide={() => setShowPremiumModal(false)}
          locale={locale}
          hasPremium={selectedReservation?.magicServices !== ""}
        />
      ) : null}
    </div>
  );
}